import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

// Restos de la versión anterior, en la que el flag de registro se persistía en
// localStorage y sobrevivía a la sesión (y al usuario) del navegador.
const LEGACY_STORAGE_REGISTERED = 'operator_registered_in_campaign'

// queue_monitor_loop publica QueueMemberGrouped cada 3 s por cada agente que sea
// miembro de alguna cola. Diez ciclos de margen absorben un tick perdido y la
// reconexión de AMI (unos 13 s sin eventos) sin mostrar al agente como fuera de
// campaña, y sin sostener demasiado tiempo un registro que ya no existe.
const VOICE_HEARTBEAT_TTL_MS = 30_000

// Al abrir el websocket esperamos menos: dos ciclos bastan para que un agente ya
// registrado se confirme, y así el botón no se queda girando treinta segundos en
// cada carga de página cuando sencillamente no hay registro que confirmar.
const VOICE_FIRST_EVENT_GRACE_MS = 6_000

// Ventana tras un registro exitoso: AMI puede tener que conectarse antes de que
// salga el primer poll, así que el botón se queda "cargando" en vez de volver a
// decir "Iniciar campaña" mientras llega la confirmación real.
const VOICE_REGISTRATION_PENDING_MS = 15_000

// Tras una baja aceptada por el backend no se apaga el flag a mano: se acorta el
// plazo del heartbeat. Si el QueueRemove no prosperó en Asterisk el evento sigue
// llegando y el registro se sostiene (que es la verdad); si de verdad salió, el
// botón vuelve en un ciclo y medio en vez de esperar el TTL completo.
const VOICE_UNREGISTRATION_CONFIRM_MS = 5_000

// Estado de registro en campaña del agente, compartido entre AppHeader,
// AppSidebarMobile y WebphoneDraggable.
//
// Fuentes de verdad, una por canal:
//   - VOZ:  el evento QueueMemberGrouped del monitor filtrado por la propia
//           extensión. Mientras llega, el agente es miembro de la cola en
//           Asterisk; cuando deja de llegar (expulsión, salida de campaña, AMI
//           caído) el registro expira solo. No se infiere de la respuesta del
//           botón ni se persiste entre cargas: si nadie lo confirma, es false.
//   - CHAT: campaign_logins vía GET session-status (useCampaignRegistrationSync),
//           porque un agente de solo chat no es miembro de ninguna cola y por
//           tanto Asterisk nunca lo reporta.
export const useCampaignRegistrationStore = defineStore('campaignRegistration', () => {
  try {
    localStorage.removeItem(LEGACY_STORAGE_REGISTERED)
  } catch {
    // Sin localStorage el flag simplemente no existe; nada que limpiar.
  }

  const isRegisteredInVoice = ref(false)
  const isVoiceStateKnown = ref(false)
  const isVoiceRegistrationPending = ref(false)
  const isRegisteredInChat = ref(false)
  const isChatStateKnown = ref(false)
  const isChatStatusLoading = ref(false)
  const campaignAssignmentChanged = ref(false)

  // Aviso de "te expulsaron de tus campañas". Vive acá y no en el componente
  // del diálogo porque llega por dos caminos distintos —QueueMemberEjected del
  // webphone y AGENT_EJECTED_FROM_CAMPAIGNS del socket de chat—: ninguno cubre
  // a todos los agentes, y a quien le lleguen los dos tiene que ver un solo
  // aviso.
  const isEjectionNoticeVisible = ref(false)

  const isClassificationFormActive = ref(false)
  const isCampaignExitScheduled = ref(false)
  const scheduledExitRequiresFormSave = ref(false)
  const scheduledExitFormSaved = ref(false)
  const isProcessingScheduledCampaignExit = ref(false)

  let voiceHeartbeatTimer: ReturnType<typeof setTimeout> | null = null
  let voicePendingTimer: ReturnType<typeof setTimeout> | null = null

  const clearVoiceHeartbeatTimer = () => {
    if (voiceHeartbeatTimer === null) return
    clearTimeout(voiceHeartbeatTimer)
    voiceHeartbeatTimer = null
  }

  const clearVoicePendingTimer = () => {
    if (voicePendingTimer === null) return
    clearTimeout(voicePendingTimer)
    voicePendingTimer = null
  }

  const expireVoiceRegistration = () => {
    voiceHeartbeatTimer = null
    isVoiceStateKnown.value = true
    isRegisteredInVoice.value = false
  }

  /** Llegó un QueueMemberGrouped propio: el agente está en la cola, ahora. */
  const markVoiceHeartbeat = () => {
    clearVoicePendingTimer()
    isVoiceRegistrationPending.value = false
    isVoiceStateKnown.value = true
    isRegisteredInVoice.value = true

    clearVoiceHeartbeatTimer()
    voiceHeartbeatTimer = setTimeout(expireVoiceRegistration, VOICE_HEARTBEAT_TTL_MS)
  }

  /**
   * Se abrió el websocket de monitor. Arranca el reloj para que, si no llega
   * ningún evento, el estado pase de "desconocido" a "no registrado" en vez de
   * quedarse colgado esperando para siempre.
   */
  const beginVoiceHeartbeatWatch = () => {
    if (isVoiceStateKnown.value || voiceHeartbeatTimer !== null) return
    voiceHeartbeatTimer = setTimeout(expireVoiceRegistration, VOICE_FIRST_EVENT_GRACE_MS)
  }

  /** El POST de registro salió bien; falta que Asterisk lo confirme por evento. */
  const markVoiceRegistrationPending = () => {
    isVoiceRegistrationPending.value = true
    clearVoicePendingTimer()
    voicePendingTimer = setTimeout(() => {
      voicePendingTimer = null
      isVoiceRegistrationPending.value = false
    }, VOICE_REGISTRATION_PENDING_MS)
  }

  /** El POST de baja salió bien; se le da un ciclo corto a Asterisk para confirmarla. */
  const expectVoiceUnregistration = () => {
    clearVoicePendingTimer()
    isVoiceRegistrationPending.value = false
    if (!isRegisteredInVoice.value) return

    clearVoiceHeartbeatTimer()
    voiceHeartbeatTimer = setTimeout(expireVoiceRegistration, VOICE_UNREGISTRATION_CONFIRM_MS)
  }

  const setChatRegistered = (value: boolean) => {
    isRegisteredInChat.value = value
    isChatStateKnown.value = true
  }

  const setChatStatusLoading = (value: boolean) => {
    isChatStatusLoading.value = value
  }

  const markCampaignAssignmentChanged = () => {
    campaignAssignmentChanged.value = true
  }

  const clearCampaignAssignmentChanged = () => {
    campaignAssignmentChanged.value = false
  }

  const isRegisteredInCampaign = computed(
    () => isRegisteredInVoice.value || isRegisteredInChat.value,
  )

  const setClassificationFormActive = (value: boolean) => {
    isClassificationFormActive.value = value
  }

  const scheduleCampaignExit = (requiresFormSave: boolean) => {
    isCampaignExitScheduled.value = true
    scheduledExitRequiresFormSave.value = requiresFormSave
    scheduledExitFormSaved.value = !requiresFormSave
  }

  const markClassificationFormSaved = () => {
    isClassificationFormActive.value = false
    if (isCampaignExitScheduled.value && scheduledExitRequiresFormSave.value) {
      scheduledExitFormSaved.value = true
    }
  }

  const canProcessScheduledCampaignExit = computed(
    () =>
      isCampaignExitScheduled.value &&
      (!scheduledExitRequiresFormSave.value || scheduledExitFormSaved.value) &&
      !isProcessingScheduledCampaignExit.value,
  )

  const claimScheduledCampaignExit = () => {
    if (!canProcessScheduledCampaignExit.value) return false

    isProcessingScheduledCampaignExit.value = true
    return true
  }

  const resolveScheduledCampaignExit = (completed: boolean) => {
    if (completed) {
      isCampaignExitScheduled.value = false
      scheduledExitRequiresFormSave.value = false
      scheduledExitFormSaved.value = false
    }
    isProcessingScheduledCampaignExit.value = false
  }

  const showEjectionNotice = () => {
    isEjectionNoticeVisible.value = true
  }

  const dismissEjectionNotice = () => {
    isEjectionNoticeVisible.value = false
  }

  const resetSessionState = () => {
    clearVoiceHeartbeatTimer()
    clearVoicePendingTimer()
    isRegisteredInVoice.value = false
    isVoiceStateKnown.value = false
    isVoiceRegistrationPending.value = false
    isRegisteredInChat.value = false
    isChatStateKnown.value = false
    isChatStatusLoading.value = false
    campaignAssignmentChanged.value = false
    isEjectionNoticeVisible.value = false
    isClassificationFormActive.value = false
    isCampaignExitScheduled.value = false
    scheduledExitRequiresFormSave.value = false
    scheduledExitFormSaved.value = false
    isProcessingScheduledCampaignExit.value = false
  }

  return {
    isRegisteredInCampaign,
    isRegisteredInVoice,
    isRegisteredInChat,
    isChatStateKnown,
    isVoiceStateKnown,
    isVoiceRegistrationPending,
    isChatStatusLoading,
    campaignAssignmentChanged,
    isEjectionNoticeVisible,
    isClassificationFormActive,
    isCampaignExitScheduled,
    canProcessScheduledCampaignExit,
    markVoiceHeartbeat,
    beginVoiceHeartbeatWatch,
    markVoiceRegistrationPending,
    expectVoiceUnregistration,
    setChatRegistered,
    setChatStatusLoading,
    markCampaignAssignmentChanged,
    clearCampaignAssignmentChanged,
    setClassificationFormActive,
    scheduleCampaignExit,
    markClassificationFormSaved,
    claimScheduledCampaignExit,
    resolveScheduledCampaignExit,
    showEjectionNotice,
    dismissEjectionNotice,
    resetSessionState,
  }
})
